<template>
    <div ref="articleRef">       
        <div class="grid grid-cols-8 gap-7 items-center border-t border-black cursor-pointer"                 
            @click="$emit('update-handle', article.handle)">
            <!-- Toggle Button Column -->
            <div class="col-span-1 gap-7 flex justify-center">                
                <UiOpen class="close w-3" :class="{'open': isOpened}" :forceHover="isOpened"/>
            </div>
        
            <!-- Title Content Column -->
            <div :class="{'col-span-6': isOpened, 'col-span-4': !isOpened}" class="flex items-center">
                <h3 class="mt-4 text-lg sm:text-xl md:text-2xl lg:text-3xl" v-html="article.title"></h3>
            </div>

            <!-- Image Column (Hidden when expanded) -->
            <div v-if="!isOpened" class="col-span-3 ml-20">
                <UiArticleImage 
                    :article="article"
                    class="w-full py-2"
                    :modifiers="{
                        auto: 'format,compress',
                        fit: 'crop',
                        crop: 'faces,edges',
                        ar: '245:164'
                    }"
                />
            </div>

        </div>
  
        <!-- Expandable Content -->
        <div :class="{'open': isOpened, 'smalltext': small}" class="grid grid-cols-8 gap-7 content">
            <div class="col-span-1 hidden sm:block"></div> <!-- Empty space for alignment -->            <div class="col-span-8 sm:col-span-6 mb-8 content-html">
                <div v-html="article.contenthtml" class=""></div>
                    <div class="mt-3 flex justify-between items-center">
                    <span class="text-xs">
                        Published on {{ new Date(article.publishedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) }}
                    </span>

                    <!-- Special Share Button -->
                    <UiShareButton
                        :icon="IconShare"
                        class=" transition duration-300"
                        @click.stop="shareArticle"
                    >
                        Share
                        <IconShare class="inline-block h-5 w-5 ml-1" stroke="1.25" />
                    </UiShareButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { IconShare } from '@tabler/icons-vue';



const UiOpen = defineAsyncComponent(() => import('@/components/ui/open.vue'));

const props = defineProps({
    article: {
        type: Object,
        required: true
    },
    small: {
        type: Boolean,
        default: false,
        required: false
    },
    openedHandle: {
        type: String,
        required: false,
        default: null
    }
});

const route = useRoute();
const articleRef = ref(null);

// Computed property to check if this article should be opened
const isOpened = computed(() => props.openedHandle === props.article.handle);

// Smooth scroll function
const smoothScrollTo = (target, duration) => {
    const start = window.scrollY;
    const end = target.offsetTop;
    const distance = end - start;
    let startTime = null;

    const scroll = (currentTime) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);

        window.scrollTo(0, start + distance * progress);

        if (timeElapsed < duration) {
            requestAnimationFrame(scroll);
        }
    };

    requestAnimationFrame(scroll);
};

// Scroll to the article if it's opened
const scrollToArticle = () => {
    nextTick(() => {
        if (isOpened.value && articleRef.value) {
            smoothScrollTo(articleRef.value, 800);
        }
    });
};

// Watch for changes to openedHandle and scroll if necessary
watch(() => props.openedHandle, () => {
    scrollToArticle();
});

// Scroll if the article was opened from the URL on load
onMounted(() => {
    if (isOpened.value) {
        scrollToArticle();
    }
});

// Share article function
const shareArticle = () => {
    const shareUrl = `${window.location.origin}/articles/${props.article.handle}`;
    if (navigator.share) {
        navigator.share({
            title: props.article.title,
            url: shareUrl
        }).catch((error) => console.error('Error sharing:', error));
    } else {
        // Fallback for browsers that don't support the Web Share API
        alert(`Share this article: ${shareUrl}`);
    }
};
</script>

<style scoped>
.content {
    max-height: 0px;
    overflow: hidden;
    transition: max-height 0.3s ease, opacity 0.3s ease;
    opacity: 0;
}

.content.open {
    transition: max-height 1s ease, opacity 1s 0.3s ease;
    max-height: 4000px;
    opacity: 1;
}

.faded-out {
    opacity: 0;
    visibility: hidden;
    transition: opacity 1.0s ease, visibility 1.0s ease;
}

.transition-opacity {
    transition: opacity 0.5s ease-in-out;
}

@media (max-width: 640px) {
    .col-span-3 {
        margin-left: 0; /* Remove extra margin for smaller screens */
    }

    .col-span-3 .w-full {
        width: 100%; /* Make the image slightly larger */
        margin: 0 auto; /* Center the image */
    }
}

/* Videos inside article content should match text width */
::v-deep(.content-html p:has(iframe:only-child)),
::v-deep(.content-html div:has(iframe:only-child)) {
  max-width: 48rem;
  margin: 76px auto;
  text-align: center;
}

/* Style the iframe inside <p> or <div> */
::v-deep(.content-html p:has(iframe:only-child) iframe),
::v-deep(.content-html div:has(iframe:only-child) iframe) {
  width: 100%;
  max-width: 100%;
  aspect-ratio: 16 / 9;
  display: block;
  border: none;
  object-fit: cover;
}

@media (max-width: 640px) {
  ::v-deep(.content-html p:has(iframe:only-child)),
  ::v-deep(.content-html div:has(iframe:only-child)) {
    max-width: 100%;
    margin: 40px auto;
  }

  ::v-deep(.content-html p:has(iframe:only-child) iframe),
  ::v-deep(.content-html div:has(iframe:only-child) iframe) {
    width: 100%;
    max-width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    object-fit: cover;
  }
}

@media (min-width: 640px) and (max-width: 1024px) {
  ::v-deep(.content-html p:has(iframe:only-child)),
  ::v-deep(.content-html div:has(iframe:only-child)) {
    max-width: 100%; 
    margin: 60px auto;
  }

  ::v-deep(.content-html p:has(iframe:only-child) iframe),
  ::v-deep(.content-html div:has(iframe:only-child) iframe) {
    width: 100%;
    max-width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    object-fit: cover;
  }
}


</style>
